/**
 * Brand Directory Component
 * Handles category and alphabet filtering.
 */
class BrandDirectoryComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {string} */
  #activeCategory = 'all';

  /** @type {string} */
  #activeLetter = 'all';

  connectedCallback() {
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    const categoryLinks = this.querySelectorAll('.brand-directory__category-link');
    categoryLinks.forEach((link) => {
      link.addEventListener('click', (e) => this.#handleCategoryClick(e), opts);
    });

    const letterLinks = this.querySelectorAll('.brand-directory__letter-link');
    letterLinks.forEach((link) => {
      link.addEventListener('click', (e) => this.#handleLetterClick(e), opts);
    });
  }

  /**
   * @param {Event} e
   */
  #handleCategoryClick(e) {
    e.preventDefault();
    const link = /** @type {HTMLElement} */ (e.currentTarget);
    const category = link.getAttribute('data-category');
    if (!category) return;

    this.#activeCategory = category;
    this.#updateCategoryActiveState();
    this.#applyFilters();
  }

  /**
   * @param {Event} e
   */
  #handleLetterClick(e) {
    e.preventDefault();
    const link = /** @type {HTMLElement} */ (e.currentTarget);
    if (link.classList.contains('brand-directory__letter-link--disabled')) return;

    const letter = link.getAttribute('data-letter');
    if (!letter) return;

    this.#activeLetter = letter;
    this.#updateLetterActiveState();
    this.#applyFilters();
  }

  #updateCategoryActiveState() {
    const categoryLinks = this.querySelectorAll('.brand-directory__category-link');
    categoryLinks.forEach((link) => {
      const el = /** @type {HTMLElement} */ (link);
      const isActive = el.getAttribute('data-category') === this.#activeCategory;
      el.classList.toggle('brand-directory__category-link--active', isActive);
    });
  }

  #updateLetterActiveState() {
    const letterLinks = this.querySelectorAll('.brand-directory__letter-link');
    letterLinks.forEach((link) => {
      const el = /** @type {HTMLElement} */ (link);
      const isActive = el.getAttribute('data-letter') === this.#activeLetter;
      el.classList.toggle('brand-directory__letter-link--active', isActive);
    });
  }

  #applyFilters() {
    const brandItems = this.querySelectorAll('.brand-directory__brand-item');
    const letterSections = this.querySelectorAll('.brand-directory__letter-section');

    brandItems.forEach((item) => {
      const link = item.querySelector('.brand-directory__brand-link');
      if (!link) return;
      const dataCategory = link.getAttribute('data-category') || '';
      const categories = dataCategory.split('||');
      const matchesCategory =
        this.#activeCategory === 'all' ||
        categories.includes(this.#activeCategory);
      const matchesLetter =
        this.#activeLetter === 'all' || link.getAttribute('data-letter') === this.#activeLetter;
      const isVisible = matchesCategory && matchesLetter;
      /** @type {HTMLElement} */ (item).toggleAttribute('hidden', !isVisible);
    });

    letterSections.forEach((section) => {
      const sectionEl = /** @type {HTMLElement} */ (section);
      const brandItemsInSection = section.querySelectorAll('.brand-directory__brand-item');
      const hasVisibleBrands = Array.from(brandItemsInSection).some(
        (item) => !(/** @type {HTMLElement} */ (item)).hasAttribute('hidden')
      );
      sectionEl.toggleAttribute('hidden', !hasVisibleBrands);
    });
  }
}

if (!customElements.get('brand-directory-component')) {
  customElements.define('brand-directory-component', BrandDirectoryComponent);
}
